import React, { useState, useRef, useCallback, useEffect } from "react";
import { getSteps } from "./ConversationalGoalSteps";
import { api, PabSession, User } from "@/services/api";
import { useNavigate } from "react-router-dom";
import { GoalItem } from "@/types/goals";
// import { TaskItem } from "@/types/tasks";
import { useAuth } from "@/hooks/useAuth";
import { useUpdateOnboardingStep } from "@/hooks/useUpdateOnboardingStep";
import { getPrimaryRole } from "@/utils/roleUtils";
import { profileStepCompleted } from "@/utils/tracking";
import { initSessionReplay } from "@/utils/amplitude";

// Shared with GoalsMessage's "Choose 3-4 goals" / "X/4 goals" copy and its own
// selection cap -- keep both in sync from this single source.
export const MAX_SELECTED_GOALS = 4;

export const ConversationalGoalSetting: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user ? getPrimaryRole(user) : "unknown";
  const [stepIndex, setStepIndex] = useState(0);
  const [pabSession, setPabSession] = useState<PabSession | undefined>(undefined);
  const [selectedGoals, setSelectedGoals] = useState<GoalItem[]>([]);
  // const [selectedTasks, setSelectedTasks] = useState<TaskItem[]>([]); // OKT flow disabled in onboarding
  const pageTopRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      pageTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, []);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        const activeSession = await api.pabSessions.getActiveSession();
        if (activeSession) {
          setPabSession(activeSession);
          return;
        }
      } catch {
        // fall through to initialize
      }
      try {
        const newSession = await api.pabSessions.initialize();
        setPabSession(newSession);
      } catch (initError) {
        console.error("Failed to initialize PAB session:", initError);
      }
    };

    if (!pabSession) {
      initializeSession();
    }
  }, [pabSession]);

  useEffect(() => {
    scrollToTop();
  }, [stepIndex, scrollToTop]);

  useEffect(() => {
    initSessionReplay();
    profileStepCompleted({ step_name: "goal_setting_viewed", user_role: userRole });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetSelectedGoal = useCallback((goal: GoalItem, isSelected: boolean) => {
    setSelectedGoals((prev) => {
      if (isSelected) {
        const alreadySelected = prev.some((g) => g.id === goal.id);
        // Cap at 4 to match the "Choose 3-4 goals" / "X/4 goals" copy in
        // GoalsMessage -- this previously allowed a 5th selection while the
        // UI text and counter both said 4.
        if (!alreadySelected && prev.length < MAX_SELECTED_GOALS) return [...prev, goal];
        return prev;
      }
      return prev.filter((g) => g.id !== goal.id);
    });
  }, []);

  // OKT flow disabled in onboarding — handlers commented out for easy re-enable
  // const handleSetSelectedTask = useCallback((task: TaskItem, isSelected: boolean) => {
  //   setSelectedTasks((prev) => {
  //     if (isSelected) {
  //       const alreadySelected = prev.some((t) => t.id === task.id);
  //       if (!alreadySelected) return [...prev, task];
  //       return prev;
  //     }
  //     return prev.filter((t) => t.id !== task.id);
  //   });
  // }, []);

  // const handleConfirmMyTasks = useCallback((onNext: () => void) => {
  //   profileStepCompleted({ step_name: "tasks_confirmed", user_role: userRole });
  //   onNext();
  // }, [userRole]);

  const { updateStep } = useUpdateOnboardingStep();

  const handleProceedToAdvisors = useCallback(async () => {
    await updateStep("onboarding/advisor-matching");
    profileStepCompleted({ step_name: "goal_setting_completed", user_role: userRole });
    navigate("/onboarding/advisor-matching", { state: { pabSessionId: pabSession?.id } });
  }, [navigate, pabSession, updateStep, userRole]);

  const handleConfirmMyGoals = useCallback(async () => {
    if (pabSession?.id && selectedGoals.length > 0) {
      try {
        await api.pabSessions.saveSelectedOkrs(pabSession.id, selectedGoals);
      } catch (err) {
        console.error("Failed to save selected OKRs:", err);
      }
    }
    profileStepCompleted({ step_name: "goals_confirmed", user_role: userRole });
    handleProceedToAdvisors();
  }, [pabSession, selectedGoals, userRole, handleProceedToAdvisors]);

  const handleSkipGoals = useCallback(async () => {
    await updateStep("onboarding/advisor-matching");
    profileStepCompleted({ step_name: "goal_setting_skipped", user_role: userRole });
    navigate("/onboarding/advisor-matching", { state: { pabSessionId: pabSession?.id } });
  }, [navigate, pabSession, updateStep, userRole]);

  const handleNextStep = useCallback(() => {
    if (stepIndex === 0) {
      profileStepCompleted({ step_name: "get_started_clicked", user_role: userRole });
      // Don't advance until pabSession is available — GoalsMessage needs it to fetch OKRs
      if (!pabSession?.id) return;
    }
    const currentSteps = getSteps(user as User | undefined);
    if (stepIndex < currentSteps.length - 1) {
      setStepIndex((prev) => prev + 1);
    }
  }, [stepIndex, user, userRole, pabSession]);

  const handleBackStep = useCallback(() => {
    setStepIndex((prev) => prev - 1);
    // When OKT flow is re-enabled, restore the skip logic:
    // const target = prev - 1;
    // return target === 2 ? 1 : target; // step 2 (SummaryMessage) is auto-proceed, skip it when going back
  }, []);

  const currentSteps = getSteps(user as User | undefined);
  const currentStep = currentSteps[stepIndex];
  const StepComponent = currentStep?.Component;

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-surface-page-bg">
      <div ref={pageTopRef} />
      <div className="flex flex-col items-center justify-start min-h-full py-12 px-6">
        <div className="w-full max-w-4xl">
          {StepComponent && (
            <StepComponent
              pabSession={pabSession}
              onNext={handleNextStep}
              onBack={handleBackStep}
              selectedGoals={selectedGoals}
              handleSetSelectedGoal={handleSetSelectedGoal}
              handleConfirmMyGoals={handleConfirmMyGoals}
              // selectedTasks={selectedTasks}          // OKT flow disabled in onboarding
              // handleSetSelectedTask={handleSetSelectedTask}
              // handleConfirmMyTasks={() => handleConfirmMyTasks(handleNextStep)}
              disabled={false}
              onContentLoaded={scrollToTop}
              handleProceedToAdvisors={handleProceedToAdvisors}
              handleSkipGoals={handleSkipGoals}
              user={user as User | undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationalGoalSetting;
